<template>
	<div class="container">
		<div class="page page-inventory">
			<!-- Header -->
			<div class="page-header flex-center">
				<h1 class="page-title mb-3">{{ $t('Finance') }}</h1>
				<div class="ml-auto">
					<daterangepicker :presets="filterDatePresets" :start="since" :end="until" @selected="setFilterDate">
					</daterangepicker>
				</div>
			</div>

			<div class="inventory-summaries row">
				<div class="col-md-6 no-gutters">

					<div class="row">
						<div class="col-md-12">
							<div class="box box--primary" style="min-height:120px;">
								<div class="m-2">
									<h4 class="text-accent mb-0" :class="{ 'is-loading': isFetching }">
										<span>{{ formatMoney(grandTotal) }}</span>
									</h4>
									<div class="d-flex">
										<span class="d-block mt-2">{{ $t('Grand Total') }}</span>
										<span class="d-block mt-2 ml-auto"
											v-tooltip="'Pembayaran Non COD + Pembayaran COD'"><i
												class="material-icons">info</i></span>
									</div>
								</div>
							</div>
						</div>
					</div>

					<div class="row">
						<div class="col-md-6 order-sm-last">
							<div class="box box--primary" style="min-height:120px;">
								<div class="m-2">
									<h4 class="text-accent mb-0" :class="{ 'is-loading': isFetching }">
										<span>{{ formatMoney(totalNonCod) }}</span>
									</h4>
									<span class="d-block mt-2">{{ $t('Pembayaran Non COD') }}</span>
								</div>
							</div>
						</div>
						<div class="col-md-6 order-sm-first">
							<div class="box box--primary" style="min-height:120px;">
								<div class="m-2">
									<h4 class="text-accent mb-0" :class="{ 'is-loading': isFetching }">
										<span>{{ formatMoney(totalCod) }}</span>
									</h4>
									<span class="d-block mt-2">{{ $t('Pembayaran COD') }}</span>
								</div>
							</div>
						</div>
					</div>

					<div class="row">
						<div class="col-md-6">
							<div class="box box--primary" style="min-height:120px;">
								<div class="m-2">
									<h4 class="text-accent mb-0" :class="{ 'is-loading': isFetching }">
										<span>{{ formatMoney(totalRts) }}</span>
									</h4>
									<div class="d-flex">
										<span class="d-block mt-2">{{ $t('Grand Total RTS') }}</span>
										<span class="d-block mt-2 ml-auto"
											v-tooltip="'Total nominal order yang berstatus RTS'"><i
												class="material-icons">info</i></span>
									</div>
								</div>
							</div>
						</div>
					</div>

				</div>

				<div class="col-md-3">
					<div class="box box--primary" style="min-height:120px;">
						<div class="m-2">
							<h4 class="text-accent mb-0" :class="{ 'is-loading': isFetching }">
								<span>{{ formatMoney(supplierBalance) }}</span>
							</h4>
							<div class="d-flex">
								<span class="d-block mt-2">{{ $t('Grand Saldo Supplier') }}</span>
								<span class="d-block mt-2 ml-auto" v-tooltip="'Total seluruh saldo supplier'"><i
										class="material-icons">info</i></span>
							</div>
						</div>
					</div>
				</div>

				<div class="col-md-3">
					<div class="box box--primary" style="min-height:120px;">
						<div class="m-2">
							<h4 class="text-accent mb-0" :class="{ 'is-loading': isFetching }">
								<span>{{ formatMoney(walletTotal) }}</span>
							</h4>
							<span class="d-block mt-2">{{ $t('Total Wallet') }}</span>
						</div>
					</div>
				</div>

			</div>

		</div>
	</div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import dayjs from 'dayjs';
import Daterangepicker from '@/components/Daterangepicker.vue';
import {
	duplicateVar,
	getDateRange,
} from '@/lib/helper';
import statisticApi from '@/api/statistic';
import walletApi from '@/api/wallet';

const defaultFilterDate = getDateRange();

export default {
	name: 'AdminFinance',
	components: {
		Daterangepicker,
	},
	data() {
		return {
			keyword: '',
			isFetching: false,
			filterDate: defaultFilterDate,
			walletTotal: 0,
			supplierBalance: 0,
			source: null,
			grandTotal: 0,
			totalNonCod: 0,
			totalCod: 0,
			totalRts: 0,
			since: '',
			until: '',
		};
	},
	methods: {
		...mapActions({
		}),
		fetchFinanceStats() {
			this.isFetching = true;
			const params = this.fetchParams;
			const callback = (response) => {
				const responseData = response.data;
				this.totalNonCod = responseData.sum_non_cod;
				this.totalCod = responseData.sum_cod;
				this.totalRts = responseData.sum_rts;
				this.grandTotal = this.totalNonCod + this.totalCod;
				this.isFetching = false;
			};
			const errorCallback = () => {
				this.isFetching = false;
			};

			statisticApi.getAdminFinanceStats(params, callback, errorCallback);
		},
		fetchWalletsTotal() {
			this.isFetching = true;
			const callback = (response) => {
				this.walletTotal = response.data;
			};
			const errorCallback = () => {
				this.isFetching = false;
			};

			walletApi.getWalletTotal(callback, errorCallback);
		},
		fetchWalletsTotalBalance() {
			this.isFetching = true;
			const callback = (response) => {
				this.supplierBalance = response.data;
				this.isFetching = false;
			};
			const errorCallback = () => {
				this.isFetching = false;
			};

			walletApi.getWalletBalance(callback, errorCallback);
		},
		init() {
			this.initFilterDate();
			this.fetchFinanceStats();
			this.fetchWalletsTotal();
			this.fetchWalletsTotalBalance();
		},
		setFilterDate(filterDate) {
			this.filterDate = filterDate;
			const filterDateFormat = 'YYYY-MM-DD';
			const since = dayjs(filterDate.start).format(filterDateFormat);
			const until = dayjs(filterDate.end).format(filterDateFormat);
			const query = duplicateVar(this.$route.query);
			query.since = since;
			query.until = until;
			const params = { query };
			this.$router.push(params);
			this.fetchFinanceStats();
		},
		initFilterDate() {
			if (this.since && this.since !== '' && this.until && this.until !== '') {
				const format = 'YYYY-MM-DD';
				const since = dayjs(this.since).format(format);
				const until = dayjs(this.until).format(format);
				const filterDate = {
					start: since,
					end: until,
				};
				this.filterDate = filterDate;
			}
		},
	},
	computed: {
		...mapGetters({
			user: 'user',
			filterDatePresets: 'filterDatePresets',
		}),
		fetchParams() {
			const dateFormat = 'YYYY-MM-DD';
			const startDate = dayjs(this.filterDate.start).format(dateFormat);
			const endDate = dayjs(this.filterDate.end).format(dateFormat);

			const params = {
				since: startDate,
				until: endDate,
			};

			const keyword = this.keyword.trim();
			if (keyword && keyword !== '') {
				params.keyword = keyword;
			}

			return params;
		},
	},
	watch: {
		user() {
			this.init();
		},
	},
	created() {
		if (this.user) {
			this.init();
		}
	},
};
</script>
